import DateFnsUtils from '@date-io/date-fns';
import { yupResolver } from '@hookform/resolvers';
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents';
import { AccountFooter } from '@ifca-root/react-component/src/components/Footer/AccountFooter';
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader';
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { Checkbox, TextField } from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { Autocomplete } from '@material-ui/lab';
import { CommonYupValidation } from 'helpers/Form/YupValidation';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router';
import * as yup from 'yup';
import {
  useGetAssignedCompanyQuery,
  useGetCompanyNameQuery,
  useGetDebtorAccountListingLazyQuery,
} from '@account-root/account-react/src/generated/graphql';

interface AmortisationScheduleParamsProps {
  CompanyID: string;
  InvoiceCategory: string;
  AmortStartDate: Date;
  AmortEndDate: Date;
  DebtorAccountID: string
}

interface CompanyOption {
  Name: string;
  CompanyID: string;
}

export const AmortisationScheduleParamsForm = (props: any) => {
  let history = useHistory();
  const user = JSON.parse(localStorage.getItem('loggedInUser'));
  const { CompanyID }: any = useParams();
  const latestCompany = localStorage.getItem('latestCompany');
  const ParamsSchema = yup.object().shape({
    CompanyID: CommonYupValidation.requireField('Company is required'),
    AmortStartDate: yup.string().required('Amort Start Date is required'),
    AmortEndDate: yup.string().required('Amort End Date is required'),
  });
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const {
    handleSubmit,
    register,
    errors,
    control,
    getValues,
    watch,
    setValue,
  } = useForm<AmortisationScheduleParamsProps>({
    defaultValues: {},
    mode: 'all',
    resolver: yupResolver(ParamsSchema),
  });

  const {
    loading: CompanyLoading,
    error: CompanyError,
    data: { getCompany } = { getCompany: [] },
  } = useGetCompanyNameQuery({
    variables: { CompanyID },
    fetchPolicy: 'network-only',
    onCompleted: () => {
      if (latestCompany) {
        loadDebtorAccount({ variables: { CompanyID: latestCompany } })
      }
    },
  });

  const {
    loading: CompanyAssignedLoading,
    error: CompanyAssignedError,
    data: { getAssignedCompanyByUser } = { getAssignedCompanyByUser: [] },
  } = useGetAssignedCompanyQuery({
    fetchPolicy: 'network-only',
    onCompleted: () => {
      if (latestCompany) {
        loadDebtorAccount({ variables: { CompanyID: latestCompany } })
      }
    },
  });

  const [
    loadDebtorAccount,
    {
      loading: DebtorAccountLoading,
      data: { getDebtorAccountListing } = {
        getDebtorAccountListing: [],
      },
    },
  ] = useGetDebtorAccountListingLazyQuery({
    fetchPolicy: 'network-only',
  });

  const listDebtor = getDebtorAccountListing?.sort((a, b) => {
    return a.DebtorName.localeCompare(b.DebtorName)
  })

  const InvoiceCategoryArray = [
    'SSA',
    'SAAS'
  ]

  const [selectedInvoiceCategory, setSelectedInvoiceCategory] = useState(new Set())

  const handleCheckboxInvoiceCategoryChange = (event: any) => {
    let invoiceCategory = selectedInvoiceCategory
    event?.target?.checked ? invoiceCategory.add(event?.target?.value) : invoiceCategory.delete(event?.target?.value)
    setSelectedInvoiceCategory(new Set(invoiceCategory))
  }

  const [selectedDebtorAccount, setSelectedDebtorAccount]: any = useState(
    new Set()
  )

  const handleCheckboxDebtorAccountChange = (event: any) => {
    let debtorAccount = selectedDebtorAccount

    if (event?.target?.checked) {
      debtorAccount.add(event?.target?.value)
    } else {
      debtorAccount.delete(event?.target?.value)
    }
    setSelectedDebtorAccount(new Set(debtorAccount))
  }

  const onSubmit = data => {
    history.push({
      pathname: `/digital-reports/account-receivable/amortisation-schedule-report/generated`,
      state: {
        CompanyID: data?.CompanyID,
        InvoiceCategory: selectedInvoiceCategory ? Array.from(selectedInvoiceCategory)?.toString() : undefined,
        AmortStartDate: data?.AmortStartDate ? new Date(data.AmortStartDate) : null,
        AmortEndDate: data?.AmortEndDate ? new Date(data?.AmortEndDate) : null,
        DebtorAccountID: selectedDebtorAccount ? Array.from(selectedDebtorAccount) : undefined,
      },
    });
  };

  return (
    <>
      {CompanyLoading && <Loading />}
      {CompanyAssignedLoading && <Loading />}
      {DebtorAccountLoading && <Loading />}

      <MainHeader
        mainBtn="back"
        onClick={() => history.push(`/digital-reports/account-receivable`)}
        smTitle={'Account Receivable'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Main Menu' },
          { name: 'Submenu' },
          { name: 'Amortisation Schedule Report', current: true },
        ]}
        rightRouteSegments={[{ name: 'Parameters', current: true }]}
      />

      <ContentWrapper float>
        <CardContents>
          {!CompanyAssignedLoading && !CompanyLoading && (
            <Controller
              render={({ value, onChange }) => {
                const options: CompanyOption[] =
                  !!user?.superUser === false
                    ? getAssignedCompanyByUser
                    : getCompany;
                const defVal = getCompany?.filter(
                  x => x?.CompanyID === latestCompany,
                )[0];

                return (
                  <Autocomplete
                    options={options || []}
                    getOptionLabel={(option: CompanyOption) => option.Name}
                    fullWidth
                    onChange={(value, newValue: any) => {
                      setValue('CompanyID', newValue?.CompanyID);

                      loadDebtorAccount({
                        variables: {
                          CompanyID: newValue?.CompanyID,
                        },
                      });
                    }}
                    renderOption={(props, option) => {
                      return <span>{props?.Name}</span>;
                    }}
                    defaultValue={defVal}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            helperText={errors?.CompanyID?.message}
                            error={errors?.CompanyID ? true : false}
                            label="Company"
                            style={{ width: '100%' }}
                            margin="normal"
                            required
                            defaultValue={defVal}
                          />
                        </div>
                      );
                    }}
                  />
                );
              }}
              label="Company"
              name="CompanyID"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="normal"
              ref={register}
              helperText={errors?.CompanyID?.message}
              error={errors?.CompanyID ? true : false}
              required
              defaultValue={latestCompany}
            />
          )}

          <Controller
            render={({ value, onChange }) => {
              return (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={listDebtor || []}
                  getOptionLabel={option => option?.DebtorName}
                  fullWidth
                  onChange={(value, newValue: any) => {
                    const selecteted = new Set()

                    newValue?.map(x => {
                      selecteted.add(x?.DebtorAccountID)
                    })

                    setSelectedDebtorAccount(new Set(selecteted))
                  }}
                  renderOption={(option, { selected }) => (
                    <React.Fragment>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        value={option?.DebtorAccountID}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        color="primary"
                        onChange={event => {
                          handleCheckboxDebtorAccountChange(event)
                        }}
                      />
                      {option?.DebtorName}
                    </React.Fragment>
                  )}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          variant="outlined"
                          style={{ width: '100%' }}
                          label="Debtor Account"
                          margin="normal"
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            name="DebtorAccountID"
            label="Debtor Account"
            margin="normal"
            autoComplete="off"
            fullWidth
            multiline={true}
            control={control}
            select
            ref={register}
          />

          <Controller
            render={({ value, onChange }) => {
              return (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={InvoiceCategoryArray || []}
                  getOptionLabel={option => `${option}`}
                  fullWidth
                  onChange={(value, newValue: any) => {
                    const selected = new Set();

                    newValue?.map(x => {
                      selected.add(x);
                    });

                    setSelectedInvoiceCategory(new Set(selected));
                  }}
                  renderOption={(option, { selected }) => (
                    <React.Fragment>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        value={option}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        color="primary"
                        onChange={event => {
                          handleCheckboxInvoiceCategoryChange(event);
                        }}
                      />
                      {`${option}`}
                    </React.Fragment>
                  )}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          variant="outlined"
                          style={{ width: '100%' }}
                          label="Invoice Category"
                          margin="normal"
                        />
                      </div>
                    );
                  }}
                />
              );
            }}
            name="InvoiceCategory"
            label="Invoice Category"
            margin="normal"
            autoComplete="off"
            fullWidth
            multiline={true}
            control={control}
            select
            ref={register}
          />

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              as={KeyboardDatePicker}
              name="AmortStartDate"
              label="Amort Start Date"
              control={control}
              onChange={(date: Date | null) => {}}
              format="dd/MM/yyyy"
              value={watch('AmortStartDate')}
              margin="normal"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              className="left"
              defaultValue={new Date()}
              helperText={errors?.AmortStartDate?.message}
              error={errors?.AmortStartDate ? true : false}
              showTodayButton
              fullWidth
            />
          </MuiPickersUtilsProvider>

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              as={KeyboardDatePicker}
              name="AmortEndDate"
              label="Amort End Date"
              control={control}
              onChange={(date: Date | null) => {
                setValue('AmortEndDate', date);
              }}
              format="dd/MM/yyyy"
              value={watch('AmortEndDate')}
              margin="normal"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              className="right"
              defaultValue={new Date()}
              helperText={errors?.AmortEndDate?.message}
              error={errors?.AmortEndDate ? true : false}
              showTodayButton
              fullWidth
            />
          </MuiPickersUtilsProvider>
        </CardContents>

        <AccountFooter
          options={[
            {
              name: 'Submit',
              onClick: () => {
                handleSubmit(onSubmit)();
              },
              color: 'primary',
            },
          ]}
        />
      </ContentWrapper>
    </>
  );
};
